import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { NAV_LINKS } from "../data/plans";

const NavItem = ({ link, onNavigate }) => {
  const baseClass = link.special
    ? "group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white"
    : "relative px-3 py-2 text-sm font-medium rounded-lg";
  const baseStyle = link.special
    ? {
        background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
        boxShadow: "0 8px 22px rgba(2,132,199,0.28)",
        transition: "transform 0.2s ease",
      }
    : { color: "var(--osx-text-muted)", transition: "color 0.2s ease" };

  const handleHoverEnter = (e) => {
    if (link.special) {
      e.currentTarget.style.transform = "translateY(-1px)";
    } else {
      e.currentTarget.style.color = "var(--osx-brand-600)";
    }
  };
  const handleHoverLeave = (e) => {
    if (link.special) {
      e.currentTarget.style.transform = "translateY(0)";
    } else {
      e.currentTarget.style.color = "var(--osx-text-muted)";
    }
  };

  const content = (
    <>
      {link.special && <Sparkles size={14} strokeWidth={2.6} />}
      <span>{link.label}</span>
    </>
  );

  if (link.to) {
    return (
      <Link
        to={link.to}
        data-testid={link.testId}
        className={baseClass}
        style={baseStyle}
        onClick={onNavigate}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}
      >
        {content}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      target={link.href?.startsWith("http") ? "_blank" : undefined}
      rel={link.href?.startsWith("http") ? "noreferrer noopener" : undefined}
      data-testid={link.testId}
      className={baseClass}
      style={baseStyle}
      onClick={onNavigate}
      onMouseEnter={handleHoverEnter}
      onMouseLeave={handleHoverLeave}
    >
      {content}
    </a>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="sticky top-0 z-50 backdrop-blur-xl"
      style={{
        background: "color-mix(in oklab, var(--osx-bg) 72%, transparent)",
        borderBottom: "1px solid var(--osx-border)",
      }}
      data-testid="site-header"
    >
      <div className="osx-container flex items-center justify-between py-3.5">
        <Logo />

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavItem key={link.to || link.href} link={link} />
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            aria-label="Abrir menú"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border"
            style={{ borderColor: "var(--osx-border-strong)", background: "var(--osx-surface-2)", color: "var(--osx-text)" }}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden"
            style={{ borderTop: "1px solid var(--osx-border)", background: "var(--osx-surface)" }}
            data-testid="mobile-menu"
          >
            <div className="osx-container py-3 flex flex-col gap-1">
              {NAV_LINKS.map((link) => {
                const testId = `${link.testId}-mobile`;
                const styleSpecial = {
                  color: link.special ? "#ffffff" : "var(--osx-text)",
                  background: link.special
                    ? "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))"
                    : "transparent",
                };
                const prefix = link.special ? "✨ " : "";
                if (link.to) {
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      data-testid={testId}
                      onClick={() => setOpen(false)}
                      className="px-3 py-3 text-base font-semibold rounded-lg"
                      style={styleSpecial}
                    >
                      {prefix}{link.label}
                    </Link>
                  );
                }
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={testId}
                    onClick={() => setOpen(false)}
                    className="px-3 py-3 text-base font-semibold rounded-lg"
                    style={styleSpecial}
                  >
                    {prefix}{link.label}
                  </a>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
